/* @flow */
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import idx from 'idx';

import ChatActions from 'chat/actions/ChatActions';
import ConfirmDialog from 'components/ConfirmDialog';
import Checkbox from 'components/Checkbox';
import EventTracking from 'lib/EventTracking';
import Legend from 'components/Legend';
import SettingsContainer from './SettingsContainer';
import ToastActions from 'actions/ToastActions';
import { mountModal } from 'lib/modal';
import GET_TWO_WAY_CHAT_PREF_QUERY from '../queries/TwoWayChatPreferenceQuery.gql';
import UPDATE_TWO_WAY_CHAT_PREF_MUTATION from '../queries/UpdateTwoWayChatPreferenceMutation.gql';

import type {
  UpdateTwoWayMessagingPrefVariables,
  UpdateTwoWayMessagingPref_updateUserPrefs,
} from '../queries/__generated__/UpdateTwoWayMessagingPref';
import type { TwoWayChatPreference as TwoWayChatPreferenceData } from '../queries/__generated__/TwoWayChatPreference';

type Props = {
  mutate: ({ variables: UpdateTwoWayMessagingPrefVariables }) => Promise<{
    data: UpdateTwoWayMessagingPref_updateUserPrefs,
  }>,
  data: ReactApolloData<TwoWayChatPreferenceData>,
};

class TwoWayChatPreference extends React.Component<Props> {
  handleTwoWayMessagingChange = (event: *) => {
    const twoWayMessaging = event.target.checked;
    const savePreference = () => {
      const saveOptions = [this.props.mutate({ variables: { input: twoWayMessaging } })];
      saveOptions.push(
        twoWayMessaging
          ? ChatActions.enableRepliesForAllChats()
          : ChatActions.disableRepliesForAllChats()
      );
      Promise.all(saveOptions)
        .then(() => ToastActions.showSuccessToast('Message options have been saved!'))
        .catch(ToastActions.showDefaultErrorToast);
    };

    const modal = (
      <ConfirmDialog
        eventScreenName="TwoWayChatPreference"
        title={
          'Are you sure you want to prevent people from replying to your announcements and starting conversations with you?'
        }
        subtext={'This will apply to all existing and new conversations.'}
        actionButtonText="Prevent replies"
        cancelButtonText="Cancel"
        size="small"
        onConfirm={() => {
          savePreference();
          EventTracking.trackKey(
            'account_settings.disable_two_way_confirm_modal.prevent_replies.click'
          );
        }}
      />
    );

    if (twoWayMessaging) {
      savePreference();
    } else {
      EventTracking.trackKey('account_settings.disable_two_way.checkbox.click');
      mountModal(modal, this);
    }
  };

  render() {
    return (
      <div>
        <Legend>
          Message options
          <p>Choose how you would like to communicate with your students and parents.</p>
        </Legend>
        <SettingsContainer>
          <Checkbox
            checked={idx(this.props.data.me, _ => _.preferences.two_way_messaging)}
            onChange={this.handleTwoWayMessagingChange}
            label="Allow replies to your messages"
          />
        </SettingsContainer>
      </div>
    );
  }
}

const withData = graphql(GET_TWO_WAY_CHAT_PREF_QUERY);
const withMutation = graphql(UPDATE_TWO_WAY_CHAT_PREF_MUTATION);

export { TwoWayChatPreference };
export default compose(
  withData,
  withMutation
)(TwoWayChatPreference);
